"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useAppContext } from "@/context/AppContext";
import { Eye, EyeOff, Mail, Copy, Check } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { motion } from "framer-motion";

export default function SignupForm({ onSwitch }: { onSwitch?: () => void }) {
  const router = useRouter();
  const { toast } = useToast();
  const { plans, startFreeTrial } = useAppContext();

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [copied, setCopied] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    companyName: "",
    contactPhone: "",
    country: "",
    city: "",
    plan: "Free Trial",
    subscriptionType: "monthly" as "monthly" | "annual",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  /* CLIENT-SIDE VALIDATION */
  const validateSignupForm = () => {
    const {
      name,
      email,
      password,
      confirmPassword,
      companyName,
      contactPhone,
      country,
      city,
    } = formData;

    if (
      !name ||
      !email ||
      !password ||
      !confirmPassword ||
      !companyName ||
      !contactPhone ||
      !country ||
      !city
    ) {
      toast({
        title: "Missing fields",
        description: "Please complete all required fields.",
        variant: "destructive",
      });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return false;
    }

    if (password.length < 6) {
      toast({
        title: "Weak password",
        description: "Password must be at least 6 characters long.",
        variant: "destructive",
      });
      return false;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Password mismatch",
        description: "Passwords do not match.",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  /* BACKEND ERROR HANDLER */
  const handleBackendError = (err: any) => {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      "Something went wrong.";

    toast({
      title: "Signup Error",
      description: msg,
      variant: "destructive",
    });
  };

  /* HANDLE SUBMIT */
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (formData.plan !== "Free Trial") {
      return setShowSupportDialog(true);
    }

    if (!validateSignupForm()) return;

    setIsLoading(true);

    try {
      const ok = await startFreeTrial({
        name: formData.companyName,
        email: formData.email,
        password: formData.password,
        plan: formData.plan,
        subscriptionType: formData.subscriptionType,
        contactName: formData.name,
        contactPhone: formData.contactPhone,
        country: formData.country,
        city: formData.city,
      });

      if (ok) {
        toast({
          title: "Account created",
          description: "You can now log in.",
          variant: "success",
        });
        router.push("/login");
      } else {
        toast({
          title: "Signup failed",
          description: "Unable to create your account, User Already Exist.",
          variant: "destructive",
        });
      }
    } catch (err: any) {
      handleBackendError(err);
    } finally {
      setIsLoading(false);
    }
  };

  /* COPY EMAIL */
  const handleCopyEmail = () => {
    navigator.clipboard.writeText("support@checkinoutpro.com");
    setCopied(true);
    toast({
      title: "Copied!",
      description: "Email address copied to clipboard.",
      variant: "success",
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const orderedPlans = ["Free Trial", "Basic", "Standard", "Professional"];
  const filteredPlans = orderedPlans
    .map((n) => plans.find((p) => p.title === n))
    .filter(Boolean);
  const selectedPlan =
    filteredPlans.find((p) => p && p.title === formData.plan) ??
    filteredPlans[0];

  return (
    <>
      {/* Card */}
      <Card className="relative mx-auto w-full max-w-md overflow-hidden rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/40 dark:backdrop-blur-2xl shadow-lg transition-all duration-300">
        <CardHeader className="text-center border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/30 dark:backdrop-blur-md">
          <CardTitle className="text-2xl font-semibold text-gray-900 dark:text-white">
            Create Your Account
          </CardTitle>
          <CardDescription className="text-gray-600 dark:text-gray-400">
            Start your 7-day free trial. No credit card required.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-5 p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Basic fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Your Name</Label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700"
                />
              </div>
            </div>

            {/* Passwords */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Password</Label>
                <div className="relative">
                  <Input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>
              </div>
              <div>
                <Label>Confirm Password</Label>
                <div className="relative">
                  <Input
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showConfirmPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>
              </div>
            </div>

            {/* Company Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company Name</Label>
                <Input
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                  className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700"
                />
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  required
                  className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700"
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700"
                />
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  required
                  className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700"
                />
              </div>
            </div>

            {/* Plan */}
            <Label>Subscription Plan</Label>
            <Select
              value={formData.plan}
              onValueChange={(v) =>
                setFormData((p) => ({
                  ...p,
                  plan: v,
                  subscriptionType:
                    v === "Free Trial" ? "monthly" : p.subscriptionType,
                }))
              }
            >
              <SelectTrigger className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700">
                <SelectValue placeholder="Select a Plan" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-gray-900">
                {orderedPlans.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {formData.plan === "Free Trial" ? (
              <div className="p-3 rounded-lg bg-emerald-100 text-emerald-700 border border-emerald-300">
                7 Days Free Trial
              </div>
            ) : (
              <>
                <Label>Billing Cycle</Label>
                <Select
                  value={formData.subscriptionType}
                  onValueChange={(v: "monthly" | "annual") =>
                    setFormData((p) => ({ ...p, subscriptionType: v }))
                  }
                >
                  <SelectTrigger className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700">
                    <SelectValue placeholder="Select Billing Cycle" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-gray-900">
                    <SelectItem value="monthly">
                      Monthly ${selectedPlan?.monthlyPrice ?? 0}
                    </SelectItem>
                    <SelectItem value="annual">
                      Annual ${selectedPlan?.annualPrice ?? 0} (Save 18%)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}

            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-md active:scale-95"
              disabled={isLoading}
            >
              {isLoading ? "Creating Account..." : "Start Free Trial"}
            </Button>
          </form>
        </CardContent>

        <CardFooter className="text-center">
          <p className="text-sm text-gray-700 dark:text-gray-400">
            Already have an account?{" "}
            <button
              onClick={onSwitch}
              className="text-emerald-600 hover:underline font-medium"
            >
              Log in
            </button>
          </p>
        </CardFooter>
      </Card>

      {/* Dialog */}
      <Dialog open={showSupportDialog} onOpenChange={setShowSupportDialog}>
        <DialogContent className="max-w-sm border rounded-2xl text-center">
          <DialogHeader>
            <DialogTitle>Contact Support</DialogTitle>
            <DialogDescription>
              To activate paid plans, please contact our support team.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center gap-3 mt-4">
            <a
              href="mailto:support@checkinoutpro.com"
              className="flex items-center gap-2 text-emerald-600 font-medium"
            >
              <Mail className="w-4 h-4" /> support@checkinoutpro.com
            </a>

            <Button
              onClick={handleCopyEmail}
              variant="outline"
              className={`flex items-center gap-2 ${
                copied ? "bg-emerald-100 text-emerald-700" : ""
              }`}
            >
              {copied ? (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-2"
                >
                  <Check className="w-4 h-4" /> Copied!
                </motion.span>
              ) : (
                <>
                  <Copy className="w-4 h-4" /> Copy Email
                </>
              )}
            </Button>
          </div>

          <DialogFooter>
            <Button
              onClick={() => setShowSupportDialog(false)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
